import { CounterpartyFieldReviewDto } from "@common/schemas/counterpartyFieldReview.schema";
import { GenericCounterpartyReviewField } from "@common/types/counterpartyFieldReview";
import {
    addCounterpartyFieldReviews,
    revokeCounterpartyFieldReview,
    updateCounterpartyFieldReviewsInCache,
} from "@api/counterparty.api";
import { Button } from "@design-system/Button/Button";
import { isViewOnlyRefusalError } from "@lib/viewOnlyRefusal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { CheckIcon, RevertIcon } from "@icons";
import { useUser } from "@hooks/useUser";
import { useScope } from "@state/scopeContext.lib";
import classNames from "classnames";
import { useCallback, useMemo, useState } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    isCounterpartyFieldReviewedByUser,
    mergeCounterpartyFieldReviews,
    removeCounterpartyFieldReview,
} from "./counterpartyFieldReviewUtils";

interface CounterpartyFieldReviewButtonProps {
    counterpartyId: string;
    fieldReviews: CounterpartyFieldReviewDto[] | undefined;
    field: GenericCounterpartyReviewField;
    reviewTooltip: string;
    undoTooltip?: string;
    className?: string;
}

export const CounterpartyFieldReviewButton = ({
    counterpartyId,
    fieldReviews,
    field,
    reviewTooltip,
    undoTooltip = "Undo review",
    className,
}: CounterpartyFieldReviewButtonProps) => {
    const user = useUser();
    const { workspaceId } = useScope();
    const { error: errorToast } = useToaster();
    const [isReviewOperationInFlight, setIsReviewOperationInFlight] =
        useState(false);
    const isReviewed = useMemo(
        () => isCounterpartyFieldReviewedByUser(fieldReviews, field, user?.id),
        [fieldReviews, field, user?.id],
    );

    const handleClick = useCallback(async () => {
        if (isReviewOperationInFlight || !workspaceId) {
            return;
        }

        setIsReviewOperationInFlight(true);
        try {
            if (isReviewed) {
                await revokeCounterpartyFieldReview(
                    workspaceId,
                    counterpartyId,
                    field,
                );
                updateCounterpartyFieldReviewsInCache(
                    workspaceId,
                    counterpartyId,
                    (existing) =>
                        removeCounterpartyFieldReview(
                            existing,
                            field,
                            user?.id,
                        ),
                );
                return;
            }

            const reviews = await addCounterpartyFieldReviews(
                workspaceId,
                counterpartyId,
                [field],
            );
            updateCounterpartyFieldReviewsInCache(
                workspaceId,
                counterpartyId,
                (existing) => mergeCounterpartyFieldReviews(existing, reviews),
            );
        } catch (error) {
            if (isViewOnlyRefusalError(error)) {
                return;
            }
            errorToast(
                isReviewed
                    ? "Failed to undo field review. Please try again."
                    : "Failed to mark field as reviewed. Please try again.",
            );
        } finally {
            setIsReviewOperationInFlight(false);
        }
    }, [
        isReviewOperationInFlight,
        workspaceId,
        isReviewed,
        counterpartyId,
        field,
        user?.id,
        errorToast,
    ]);

    return (
        <Tooltip
            key={isReviewed ? "reviewed" : "unreviewed"}
            content={isReviewed ? undoTooltip : reviewTooltip}
        >
            <Button
                variant={isReviewed ? "primary" : "accent"}
                size="md"
                iconOnly
                className={classNames(
                    viewTableStyles.showOnCellHover,
                    className,
                )}
                aria-label={isReviewed ? undoTooltip : reviewTooltip}
                onClick={handleClick}
                disabled={isReviewOperationInFlight}
            >
                {isReviewed ? <RevertIcon /> : <CheckIcon />}
            </Button>
        </Tooltip>
    );
};
